import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapModal.css";
import Modal from "../Modal/Modal";
import { add_client_coordinates } from "../../context/Services/Clients";
import swal from "sweetalert";

// Fix for default marker icons not showing in React/Leaflet
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

let DefaultIcon = L.icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

// Green icon for the temporary "pending" marker
const PendingIcon = L.icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
    className: "pending_marker",
});

L.Marker.prototype.options.icon = DefaultIcon;

// Component to handle map click events
const MapClickHandler = ({ onMapClick }) => {
    useMapEvents({
        click(e) {
            onMapClick([e.latlng.lat, e.latlng.lng]);
        },
    });
    return null;
};

const MapModal = ({ show, onClose, clientName, coordinates, editable, clientId, onCoordinateAdded }) => {
    const [pendingCoord, setPendingCoord] = useState(null);
    const [saving, setSaving] = useState(false);

    if (!show) return null;

    const hasCoordinates = coordinates && coordinates.length > 0;

    // Calculate center: use first existing coordinate, pending coord, or default to Morocco
    const center = hasCoordinates
        ? [coordinates[0].coordinates[0], coordinates[0].coordinates[1]]
        : pendingCoord
            ? pendingCoord
            : [33.5731, -7.5898]; // Casablanca default

    const handleMapClick = (coords) => {
        if (editable) {
            setPendingCoord(coords);
        }
    };

    const handleSave = async () => {
        if (!pendingCoord || !clientId) return;

        setSaving(true);
        try {
            const res = await add_client_coordinates(clientId, pendingCoord);
            if (res?.message === "COORDINATE_ALREADY_EXISTS") {
                swal("Info", "Ce lieu existe déjà (dans un rayon de 100m)", "info");
            } else {
                swal("Succès", "Lieu de livraison ajouté", "success");
            }
            setPendingCoord(null);
            if (onCoordinateAdded) onCoordinateAdded();
        } catch (e) {
            console.error(e);
            swal("Erreur", e?.response?.data?.message || "Erreur lors de l'ajout", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setPendingCoord(null);
        onClose();
    };

    return (
        <Modal
            showBakcDrop={show}
            modal={show}
            hideModal={handleClose}
            className="map_modal_container"
        >
            <div className="map_header">
                <h4>{clientName} - Lieux de Livraison</h4>
                {editable && (
                    <p className="map_instruction">
                        📍 Cliquez sur la carte pour ajouter un lieu de livraison
                    </p>
                )}
            </div>
            <div className="map_body">
                <MapContainer
                    key={`map-${coordinates?.length || 0}`}
                    center={center}
                    zoom={hasCoordinates ? 13 : 6}
                    scrollWheelZoom={true}
                    style={{ height: "400px", width: "100%", borderRadius: "8px" }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {editable && <MapClickHandler onMapClick={handleMapClick} />}

                    {/* Existing saved markers */}
                    {hasCoordinates && coordinates.map((drop, index) => (
                        <Marker
                            key={index}
                            position={[drop.coordinates[0], drop.coordinates[1]]}
                        >
                            <Popup>
                                <div className="map_popup">
                                    {drop.delivery && (
                                        <>
                                            <strong>Livraison:</strong> {drop.delivery?.substring(0, 8)}...
                                            <br />
                                        </>
                                    )}
                                    <strong>Date:</strong> {new Date(drop.timestamp).toLocaleString()}
                                    {!drop.delivery && <><br /><em style={{ color: "#6b7280" }}>Ajouté manuellement</em></>}
                                </div>
                            </Popup>
                        </Marker>
                    ))}

                    {/* Pending marker (not yet saved) */}
                    {pendingCoord && (
                        <Marker position={pendingCoord} icon={PendingIcon}>
                            <Popup>
                                <div className="map_popup">
                                    <strong>Nouveau lieu</strong>
                                    <br />
                                    {pendingCoord[0].toFixed(5)}, {pendingCoord[1].toFixed(5)}
                                </div>
                            </Popup>
                        </Marker>
                    )}
                </MapContainer>
            </div>

            {/* Save bar for admin */}
            {editable && pendingCoord && (
                <div className="map_save_bar">
                    <div className="map_save_coords">
                        📍 {pendingCoord[0].toFixed(5)}, {pendingCoord[1].toFixed(5)}
                    </div>
                    <div className="map_save_actions">
                        <button
                            className="map_cancel_btn"
                            onClick={() => setPendingCoord(null)}
                            disabled={saving}
                        >
                            Annuler
                        </button>
                        <button
                            className="map_save_btn"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Enregistrement..." : "Enregistrer ce lieu"}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default MapModal;
